// src/hooks/useServicesPG.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import ServicesAPI from '../services/api/serviceAPI';
import { Service } from '../models';
import { cacheManager } from '../services/cacheManager';
import { clearAllAppointmentsCache } from './useAppointmentsPG';

export function useServicesPG() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  const SERVICES_STORAGE_KEY = 'salon_services_cache';

  const saveServicesToStorage = (services) => {
    try {
      const cacheData = {
        services,
        timestamp: Date.now()
      };
      localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save services to localStorage:', error);
    }
  };

  const loadServicesFromStorage = async () => {
    try {
      const cached = localStorage.getItem(SERVICES_STORAGE_KEY);
      if (!cached) return null;

      const { services, timestamp } = JSON.parse(cached);

      // Check Firestore invalidation
      const isValid = await cacheManager.isCacheValid('services', timestamp);
      if (!isValid) {
        console.log('Services cache invalidated by Firestore');
        localStorage.removeItem(SERVICES_STORAGE_KEY);
        return null;
      }

      return services;
    } catch (error) {
      console.warn('Failed to load services from localStorage:', error);
      return null;
    }
  };

  const clearServicesStorage = () => {
    localStorage.removeItem(SERVICES_STORAGE_KEY);
  };

  const getCustomerVisibleServices = useCallback(async (options = {}) => {
    setError(null);

    try {
      console.log('Hook: Getting customer-visible services', { options });

      // Use the new API method to get customer-visible services
      const customerServices = await ServicesAPI.getCustomerVisibleServices(options);

      // Convert to Service instances
      const serviceInstances = customerServices.map(service => Service.fromJSON(service));

      console.log('Hook: Successfully retrieved customer-visible services', {
        count: serviceInstances.length,
        options
      });

      return serviceInstances;
    } catch (err) {
      console.error('Error getting customer-visible services:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Load all services (replaces direct API call)
  const loadAllServices = useCallback(async (forceReload = false) => {
    if (loading) return;

    // Check local storage first (unless forced reload)
    if (!forceReload) {
      const cachedServices = await loadServicesFromStorage();
      if (cachedServices && cachedServices.length > 0) {
        const serviceInstances = cachedServices.map(service => Service.fromJSON(service));
        setServices(serviceInstances);
        return serviceInstances;
      }
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Hook: Loading services from Firebase Functions API');
      const data = await ServicesAPI.getAll();
      const serviceInstances = data.map(service => Service.fromJSON({
        ...service,
        // Ensure consistent field mapping
        id: service.id,
        name: service.name,
        description: service.description,
        duration: service.duration,
        price: service.price,
        color: service.color,
        category: service.category,
        is_active: service.is_active,
        created_at: service.created_at,
        updated_at: service.updated_at
      }));

      setServices(serviceInstances);
      saveServicesToStorage(data);
      return serviceInstances;
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Load services only once on mount, with local storage
  useEffect(() => {
    const initializeServices = async () => {
      if (hasLoadedOnce) return;

      setHasLoadedOnce(true);
      await loadAllServices(false); // false = allow cache usage
    };

    initializeServices();
  }, []);

  // CRUD Operations - maintaining same interface as Firebase hook
  const addService = useCallback(async (serviceData) => {
    setLoading(true);
    setError(null);

    try {
      // Validate the service data
      const validation = Service.validate(serviceData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check for duplicate service names
      const nameExists = await ServicesAPI.checkNameExists(serviceData.name);
      if (nameExists) {
        throw new Error('A service with this name already exists');
      }

      // Create new service via API
      const newService = await ServicesAPI.create(serviceData);
      const serviceInstance = Service.fromJSON(newService);

      // Update local state
      setServices(prev => {
        const newList = [...prev, serviceInstance].sort((a, b) => a.name.localeCompare(b.name));
        // Update cache
        saveServicesToStorage(newList);
        return newList;
      });
      setHasLoadedOnce(false);
      await cacheManager.invalidateCache('services');
      await clearAllAppointmentsCache(); // Clear appointments cache since service names may be cached there

      return serviceInstance;
    } catch (err) {
      console.error('Error adding service:', err);
      setError(err.message);
      throw err;
    } finally {
      setHasLoadedOnce(false);
      setLoading(false);
    }
  }, []);

  const updateService = useCallback(async (serviceId, updateData) => {
    setLoading(true);
    setError(null);

    try {
      // Validate the update data
      const existingService = services.find(s => s.id === serviceId);
      if (!existingService) {
        throw new Error('Service not found');
      }

      const updatedData = { ...existingService, ...updateData };
      const validation = Service.validate(updatedData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check for duplicate service names (excluding current service)
      if (updateData.name && updateData.name !== existingService.name) {
        const nameExists = await ServicesAPI.checkNameExists(updateData.name, serviceId);
        if (nameExists) {
          throw new Error('A service with this name already exists');
        }
      }

      // Update service via API
      const updatedService = await ServicesAPI.update(serviceId, updateData);
      const serviceInstance = Service.fromJSON(updatedService);

      // Update in current state
      setServices(prev => {
        const updatedList = prev.map(service => service.id === serviceId ? serviceInstance : service)
          .sort((a, b) => a.name.localeCompare(b.name));
        // Update cache
        saveServicesToStorage(updatedList);
        return updatedList;
      });
      setHasLoadedOnce(false);
      await cacheManager.invalidateCache('services');
      await clearAllAppointmentsCache(); // Clear appointments cache since service names may be cached there

      return serviceInstance;
    } catch (err) {
      console.error('Error updating service:', err);
      setError(err.message);
      throw err;
    } finally {
      setHasLoadedOnce(false);
      setLoading(false);
    }
  }, [services]);

  const toggleServiceStatus = useCallback(async (serviceId) => {
    setLoading(true);
    setError(null);

    try {
      const existingService = services.find(s => s.id === serviceId);
      if (!existingService) {
        throw new Error('Service not found');
      }

      const updatedService = await ServicesAPI.updateStatus(serviceId, !existingService.is_active);
      const serviceInstance = Service.fromJSON(updatedService);

      // Update in current state
      setServices(prev => {
        const updatedList = prev.map(service => service.id === serviceId ? serviceInstance : service);
        // Update cache
        saveServicesToStorage(updatedList);
        return updatedList;
      });
      setHasLoadedOnce(false);
      await cacheManager.invalidateCache('services');
      await clearAllAppointmentsCache(); // Clear appointments cache since service names may be cached there

      return serviceInstance;
    } catch (err) {
      console.error('Error toggling service status:', err);
      setError(err.message);
      throw err;
    } finally {
      setHasLoadedOnce(false);
      setLoading(false);
    }
  }, [services]);

  const deleteService = useCallback(async (serviceId) => {
    setLoading(true);
    setError(null);

    try {
      await ServicesAPI.delete(serviceId);

      // Remove from current state
      setServices(prev => {
        const filteredList = prev.filter(service => service.id !== serviceId);
        // Update cache
        saveServicesToStorage(filteredList);
        return filteredList;
      });
      setHasLoadedOnce(false);
      await cacheManager.invalidateCache('services');
      await clearAllAppointmentsCache(); // Clear appointments cache since service names may be cached there

      return true;
    } catch (err) {
      console.error('Error deleting service:', err);
      setError(err.message);
      throw err;
    } finally {
      setHasLoadedOnce(false);
      setLoading(false);
    }
  }, []);

  const getService = useCallback(async (serviceId) => {
    setError(null);

    try {
      // First check local state
      const localService = services.find(s => s.id === serviceId);
      if (localService) {
        return localService;
      }

      // Fallback to database
      const service = await ServicesAPI.getById(serviceId);
      return service ? Service.fromJSON(service) : null;
    } catch (err) {
      console.error('Error getting service:', err);
      setError(err.message);
      throw err;
    }
  }, [services]);

  // Handle service submission (add or edit) - maintains same interface
  const handleServiceSubmit = useCallback(async (serviceDataOrId, modeOrData, mode) => {
    try {
      if (mode === 'edit') {
        // serviceDataOrId is the ID, modeOrData is the update data
        await updateService(serviceDataOrId, modeOrData);
        console.log('Service updated successfully');
        return { success: true, mode: 'edit' };
      } else {
        // serviceDataOrId is the new service data
        await addService(serviceDataOrId);
        console.log('Service added successfully');
        return { success: true, mode: 'add' };
      }
    } catch (error) {
      console.error('Error saving service:', error);
      throw error; // Re-throw so the calling component can handle it
    }
  }, [addService, updateService]);

  // Computed values (derived from current services)
  const serviceInstances = useMemo(() => services, [services]);

  const activeServices = useMemo(() => {
    return services.filter(service => service.is_active);
  }, [services]);

  // Compatibility methods for Firebase hook interface
  return {
    // Data - maintaining same interface as Firebase hook
    services: serviceInstances,
    activeServices,
    loading,
    error,

    // Actions - same interface as Firebase hook
    addService,
    updateService,
    toggleServiceStatus,
    deleteService,
    getService,
    clearError,
    handleServiceSubmit,

    // PostgreSQL-specific methods
    loadAllServices, // New method for explicit loading
    getCustomerVisibleServices,

    // Legacy compatibility
    serviceInstances, // For backward compatibility
  };
}